import { Grid } from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import { Alert } from "@material-ui/lab";
import { useFormikContext } from "formik";
import React, { Fragment, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { FormikTextField } from "../../../../../../../../components/FormikFields";
import FormikDatePicker from "../../../../../../../../components/FormikFields/FormikDatePicker";
import FormikModalCheckboxes from "../../../../../../../../components/FormikFields/FormikModalCheckboxes";
import useServiceHolderUniqueCifQuery from "../../../../../../../../queries/serviceHolder/useServiceHolderUniqueCifQuery";
import useDebounce from "../../../../../../../../util/useDebounce";
import FormBox from "../../../FormComponents/FormBox";
import FormBoxTitle from "../../../FormComponents/FormBoxTitle";
import FormSection from "../../../FormComponents/FormSection";
import FormSectionTitle from "../../../FormComponents/FormSectionTitle";
import { TServiceValuationForm } from "../../types";

const StyledAlert = styled(Alert)`
  font-size: 0.875rem;
  line-height: 1.35;
  margin: 0.25rem 0;
`;

interface IServiceHoldersMiniFormProps {
  pathologyOptions: { value: string; label: string }[];
  mobilityOptions: { value: string; label: string }[];
}

const ServiceHoldersMiniForm = (props: IServiceHoldersMiniFormProps) => {
  const { pathologyOptions } = props;
  const formikContext = useFormikContext<TServiceValuationForm>();
  const { formatMessage } = useIntl();
  const debouncedCif1 = useDebounce(formikContext.values.DNI_U1, 500);
  const serviceHolder1UniqueCifQuery =
    useServiceHolderUniqueCifQuery(debouncedCif1);
  const debouncedCif2 = useDebounce(formikContext.values.DNI_U2, 500);
  const serviceHolder2UniqueCifQuery =
    useServiceHolderUniqueCifQuery(debouncedCif2);

  useEffect(() => {
    const filteredServiceHolders = serviceHolder1UniqueCifQuery.data?.filter(
      (sh) => sh.id !== formikContext.values.service_holder_id
    );
    if (filteredServiceHolders && filteredServiceHolders.length > 0) {
      const serviceHolder = filteredServiceHolders[0];
      if (
        !formikContext.values.DNI_U1_used_by ||
        formikContext.values.DNI_U1_used_by.id !== serviceHolder.id
      ) {
        formikContext.setFieldValue("DNI_U1_used_by", {
          id: serviceHolder.id,
          crm_id: serviceHolder.zohoId,
          crm_id_name: serviceHolder.Name,
        });
      }
    } else {
      if (formikContext.values.DNI_U1_used_by) {
        formikContext.setFieldValue("DNI_U1_used_by", null);
      }
    }
  }, [serviceHolder1UniqueCifQuery, formikContext]);

  useEffect(() => {
    const filteredServiceHolders = serviceHolder2UniqueCifQuery.data?.filter(
      (sh) => sh.id !== formikContext.values.service_holder_id
    );
    if (filteredServiceHolders && filteredServiceHolders.length > 0) {
      const serviceHolder = filteredServiceHolders[0];
      if (
        !formikContext.values.DNI_U2_used_by ||
        formikContext.values.DNI_U2_used_by.id !== serviceHolder.id
      ) {
        formikContext.setFieldValue("DNI_U2_used_by", {
          id: serviceHolder.id,
          crm_id: serviceHolder.zohoId,
          crm_id_name: serviceHolder.Name,
        });
      }
    } else {
      if (formikContext.values.DNI_U2_used_by) {
        formikContext.setFieldValue("DNI_U2_used_by", null);
      }
    }
  }, [serviceHolder2UniqueCifQuery, formikContext]);

  return (
    <Fragment>
      {(formikContext.values.Usuarios_a_cuidar === "1" ||
        formikContext.values.Usuarios_a_cuidar === "2") && (
        <FormBox>
          <FormBoxTitle
            title={formatMessage({
              id: "ServiceValuationForm.GeneralDataU1",
              defaultMessage: "Datos Usuario U1",
            })}
            icon={<PersonIcon />}
          />
          <FormSection>
            <Grid container spacing={8}>
              {formikContext.values.DNI_U1_used_by && (
                <Grid item xs={12} md={12}>
                  <StyledAlert severity="error">
                    <FormattedMessage
                      id="ServiceValuationForm.ServiceHolderCifAlreadyInUse"
                      defaultMessage="El DNI que has introducido ya está en uso. Por favor, revísalo o usa el usuario ya existente"
                    />
                  </StyledAlert>
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Nombre_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Name"
                      defaultMessage="Nombre"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Primer_apellido_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Surname"
                      defaultMessage="Primer Apellido"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Segundo_apellido_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.SecondSurname"
                      defaultMessage="Segundo Apellido"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikDatePicker
                  name="Fecha_de_nacimiento_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Birthdate"
                      defaultMessage="Fecha de nacimiento"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="DNI_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.DNI"
                      defaultMessage="DNI"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                />
              </Grid>
              <Grid item xs={12} md={4}></Grid>
            </Grid>
          </FormSection>
          {(formikContext.values.Usuarios_a_cuidar === "1" ||
            formikContext.values.Usuarios_a_cuidar === "2") && (
            <FormSection>
              <FormSectionTitle
                title={formatMessage({
                  id: "ServiceValuationForm.PhysicalStatus",
                  defaultMessage: "Estado físico y cognitivo",
                })}
              />
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikModalCheckboxes
                    name="Patologias_U1"
                    label={
                      <FormattedMessage
                        id="ExperiencesTable.Pathologies"
                        defaultMessage="Patologías"
                      />
                    }
                    required
                    fullWidth
                    options={pathologyOptions}
                    messages={{
                      addLabel: formatMessage({
                        id: "ServiceValuationForm.AddPathology",
                        defaultMessage: "Añadir",
                      }),
                      dialogCancelButton: formatMessage({
                        id: "ServiceValutaionForm.PathologiesCancel",
                        defaultMessage: "Cancelar",
                      }),
                      dialogAcceptButton: formatMessage({
                        id: "ServiceValuationForm.PathologiesAccept",
                        defaultMessage: "Aceptar",
                      }),
                      dialogTitle: formatMessage({
                        id: "ServiceValuationForm.PathologiesModify",
                        defaultMessage: "Modificar Patologias",
                      }),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Comentarios_patologias_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.PathologiesComments"
                        defaultMessage="Comentarios patologías"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
            </FormSection>
          )}
        </FormBox>
      )}
      {formikContext.values.Usuarios_a_cuidar === "2" && (
        <FormBox>
          <FormBoxTitle
            title={formatMessage({
              id: "ServiceValuationForm.GeneralDataU2",
              defaultMessage: "Datos Usuario U2",
            })}
            icon={<PersonIcon />}
          />
          <FormSection>
            <Grid container spacing={8}>
              {formikContext.values.DNI_U2_used_by && (
                <Grid item xs={12} md={12}>
                  <StyledAlert severity="error">
                    <FormattedMessage
                      id="ServiceValuationForm.ServiceHolderCifAlreadyInUse"
                      defaultMessage="El DNI que has introducido ya está en uso. Por favor, revísalo o usa el usuario ya existente"
                    />
                  </StyledAlert>
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Nombre_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Name"
                      defaultMessage="Nombre"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Primer_apellido_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Surname"
                      defaultMessage="Primer Apellido"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Segundo_apellido_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.SecondSurname"
                      defaultMessage="Segundo Apellido"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikDatePicker
                  name="Fecha_de_nacimiento_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Birthdate"
                      defaultMessage="Fecha de nacimiento"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="DNI_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.DNI"
                      defaultMessage="DNI"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                />
              </Grid>
              <Grid item xs={12} md={4}></Grid>
            </Grid>
          </FormSection>
          {formikContext.values.Usuarios_a_cuidar === "2" && (
            <FormSection>
              <FormSectionTitle
                title={formatMessage({
                  id: "ServiceValuationForm.PhysicalStatus",
                  defaultMessage: "Estado físico y cognitivo",
                })}
              />
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikModalCheckboxes
                    name="Patologias_U2"
                    label={
                      <FormattedMessage
                        id="ExperiencesTable.Pathologies"
                        defaultMessage="Patologías"
                      />
                    }
                    fullWidth
                    options={pathologyOptions}
                    messages={{
                      addLabel: formatMessage({
                        id: "ServiceValuationForm.AddPathology",
                        defaultMessage: "Añadir",
                      }),
                      dialogCancelButton: formatMessage({
                        id: "ServiceValutaionForm.PathologiesCancel",
                        defaultMessage: "Cancelar",
                      }),
                      dialogAcceptButton: formatMessage({
                        id: "ServiceValuationForm.PathologiesAccept",
                        defaultMessage: "Aceptar",
                      }),
                      dialogTitle: formatMessage({
                        id: "ServiceValuationForm.PathologiesModify",
                        defaultMessage: "Modificar Patologias",
                      }),
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Comentarios_patologias_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.PathologiesComments"
                        defaultMessage="Comentarios patologías"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
            </FormSection>
          )}
        </FormBox>
      )}
    </Fragment>
  );
};

export default ServiceHoldersMiniForm;
